import React, { useContext, useState } from 'react';
import PageLayout from '../pagelayout';
import { useIntl } from 'react-intl';
import translations from './myinstitution.uha.i18n';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import Icon, { IconType } from '../components/icon';
import UserTableUh from './usertable.uh';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import {
  BrukerrollerDto,
  HttpError,
} from '../api/generated/studiested-generated-api';
import { UhaUserContext } from '../user/UhaUserStore';
import { ConfirmationDialog } from '../components/dialog';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import { Outlet, Route, Routes } from 'react-router-dom';
import PracticeTypes from '../practicetype/practicetypes.uha';
import PracticeTypePage from '../practicetype/practicetype.uha';
import InviteUha from '../invite/inviteUha.uha';
import StudiestedbrukerprofilPage from '../userprofile/studiestedbrukerprofilpage.uha';
import StudentbrukerprofilPage from '../userprofile/studentbrukerprofilpage.uha';
import { Tab, Tabs } from '../components/tabs/tabs';
import { PageNotFoundWithoutLayout } from '../pages/pagenotfound';

const MyInstitutionUhaPage = () => {
  const { uhaUser: bruker } = useContext(UhaUserContext);

  const { formatMessage } = useIntl();
  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    newuser: formatMessage({ id: translations.newuser.id }),
    colleagesTab: formatMessage({ id: translations.colleagesTab.id }),
    practicetypesWithReqsTab: formatMessage({
      id: translations.practicetypesWithReqsTab.id,
    }),
    practicetypesWithoutReqsTab: formatMessage({
      id: translations.practicetypesWithoutReqsTab.id,
    }),
  };

  const inviteButton: React.ReactNode = ToolbarLink({
    title: labels.newuser,
    exact: true,
    path: 'invite',
    icon: <Icon type={IconType.AddCircleRounded} />,
  });

  const canInvite =
    (bruker && bruker.hoeyesteRettighet === BrukerrollerDto.Administrator) ||
    false;

  const practicetypesSuffix = 'practicetypes';
  const withRequirementsSuffix = '/withrequirements';
  const withoutRequirementsSuffix = '/withoutrequirements';

  return (
    <Routes>
      <Route path="/practicetype/:kode/*" element={<PracticeTypePage />} />
      <Route path="/invite" element={<InviteUha />} />
      <Route
        path="/studiestedbrukerprofil/:id"
        element={<StudiestedbrukerprofilPage />}
      />
      <Route
        path="/studentbrukerprofil/:id"
        element={<StudentbrukerprofilPage />}
      />
      <Route
        path="/*"
        element={
          <PageLayout
            title={labels.title}
            subtitle={labels.subtitle}
            actionItems={canInvite ? inviteButton : null}
            isIndented
          >
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Tabs>
                      <Tab to="" end>
                        {labels.colleagesTab}
                      </Tab>
                      <Tab
                        to={`${practicetypesSuffix}${withRequirementsSuffix}`}
                      >
                        {labels.practicetypesWithReqsTab}
                      </Tab>
                      <Tab
                        to={`${practicetypesSuffix}${withoutRequirementsSuffix}`}
                      >
                        {labels.practicetypesWithoutReqsTab}
                      </Tab>
                    </Tabs>
                    <Outlet />
                  </>
                }
              >
                <Route path="" element={<Colleagues />} />
                <Route
                  path={`${practicetypesSuffix}/*`}
                  element={<PracticeTypes />}
                />
                <Route path="*" element={<PageNotFoundWithoutLayout />} />
              </Route>
            </Routes>
          </PageLayout>
        }
      />
    </Routes>
  );
};

const Colleagues = () => {
  const { brukerApi, studiestedApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const { data, loading, reload } = useLoader(
    () => studiestedApi.studiestedbrukereGet({}),
    [],
  );

  type IUserDeleteConfirmation =
    | { open: false }
    | {
        open: true;
        studiestedbrukerId: string;
        name: string;
        numActivities: number;
      };
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<IUserDeleteConfirmation>({ open: false });

  const { formatMessage } = useIntl();

  const labels = {
    confirmDeleteTitle: formatMessage({
      id: translations.confirmDeleteTitle.id,
    }),
    deleteSucceeded: formatMessage({ id: translations.deleteSucceeded.id }),
  };

  const handleDelete = (id: string, name: string) => {
    brukerApi
      .praksisaktiviteterIdGet({ pathParams: { id: id } })
      .then((aktiviteter) => {
        const numActivities = aktiviteter.fremtidige
          ? aktiviteter.fremtidige.length
          : 0;
        setDeleteConfirmation({
          open: true,
          studiestedbrukerId: id,
          name: name,
          numActivities: numActivities,
        });
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const doDelete = (ok: boolean) => {
    if (!deleteConfirmation.open) return;

    setDeleteConfirmation({ open: false });

    if (!ok) return; // the user chose to abort

    studiestedApi
      .studiestedbrukerIdDelete({
        pathParams: { id: deleteConfirmation.studiestedbrukerId },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.deleteSucceeded,
        });
        reload();
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };

  const confirmationText = () => {
    if (deleteConfirmation.open) {
      return (
        <>
          {deleteConfirmation.numActivities > 0 ? (
            <>
              <p>
                {formatMessage(
                  { id: translations.confirmDeleteUserWithActivities1.id },
                  {
                    name: deleteConfirmation.open && deleteConfirmation.name,
                  },
                )}
              </p>
              <p>
                {formatMessage(
                  { id: translations.confirmDeleteUserWithActivities2.id },
                  {
                    numActivities:
                      deleteConfirmation.open &&
                      deleteConfirmation.numActivities,
                  },
                )}
              </p>
            </>
          ) : (
            <p>
              {formatMessage(
                { id: translations.confirmDeleteUser.id },
                {
                  name: deleteConfirmation.open && deleteConfirmation.name,
                },
              )}
            </p>
          )}
        </>
      );
    }
    return '';
  };

  return (
    <>
      {loading && <Spinner />}
      {data && (
        <>
          <ConfirmationDialog
            isOpen={deleteConfirmation.open}
            onClose={doDelete}
            title={labels.confirmDeleteTitle}
          >
            {confirmationText()}
          </ConfirmationDialog>
          <UserTableUh
            users={data.filter((bruker) => bruker.fornavn !== 'SYSTEM')}
            handleDelete={handleDelete}
          />
        </>
      )}
    </>
  );
};

export default MyInstitutionUhaPage;
